import { useState, useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/feature/SEO';
import { categories, videoData } from '@/mocks/data';

function VideoCard({ video }: { video: typeof videoData[0] }) {
  return (
    <Link
      to={`/article/${video.id}`}
      className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
    >
      <div className="relative aspect-video overflow-hidden">
        <img
          src={video.thumbnail}
          alt={video.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute bottom-2 right-2 bg-black/75 backdrop-blur-sm text-white text-xs px-2 py-0.5 rounded-md">
          {video.duration}
        </div>
        <div className="absolute top-2 left-2 bg-primary-500 text-background-50 text-xs px-2 py-0.5 rounded-md">
          {video.rating}
        </div>
      </div>
      <div className="p-3 md:p-4">
        <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
          {video.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-1.5 line-clamp-2 leading-relaxed">
          {video.description}
        </p>
        <div className="flex items-center gap-3 mt-3 text-xs text-foreground-400">
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {video.views}
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'dianying');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeCategory),
    [activeCategory],
  );

  const filteredVideos = useMemo(
    () => videoData.filter((v) => v.category === activeCategory),
    [activeCategory],
  );

  const pageTitle = currentCategory
    ? `${currentCategory.name} - 黄瓜视频网页版在线观看`
    : '视频分类 - 黄瓜视频网页版';

  const pageDescription = currentCategory
    ? `${currentCategory.description}。黄瓜视频网页版免费在线观看${currentCategory.name}，无需下载注册。`
    : '黄瓜视频网页版提供丰富的视频分类，电影、电视剧、综艺、动漫、纪录片、体育等精彩内容免费观看。';

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: pageDescription,
    url: `https://www.huanggua-video.com/category/${activeCategory}`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.huanggua-video.com' },
        { '@type': 'ListItem', position: 2, name: currentCategory?.name || '分类' },
      ],
    },
  };

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDescription}
        keywords={`黄瓜视频${currentCategory?.name || '分类'},${currentCategory?.name || ''}在线观看,免费${currentCategory?.name || '视频'}`}
        canonicalUrl={`https://www.huanggua-video.com/category/${activeCategory}`}
        schema={categorySchema}
      />

      <main>
        <section className="pt-24 md:pt-28 pb-6 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <nav className="flex items-center text-xs md:text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line mx-2"></i>
                <span className="text-foreground-700 font-medium">{currentCategory?.name || '分类'}</span>
              </nav>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 pb-4 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-wrap items-center gap-2 md:gap-3 pb-6 border-b border-background-200/70 overflow-x-auto">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs md:text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-50 min-h-[400px]">
          <div className="max-w-7xl mx-auto">
            <div className="mb-6">
              <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                {currentCategory?.name} · 免费在线观看
              </h1>
              <p className="text-sm text-foreground-500 mt-1">
                {currentCategory?.description} · 共{filteredVideos.length}部作品
              </p>
            </div>

            {filteredVideos.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredVideos.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-2xl bg-background-100 mb-4">
                  <i className="ri-video-line text-3xl text-foreground-300"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无视频</p>
              </div>
            )}
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-3">
                探索更多精彩分类
              </h2>
              <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
                {categories.filter(c => c.slug !== activeCategory).map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-background-50 text-foreground-700 text-sm hover:bg-primary-50 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className={`${cat.icon} text-base`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}